import React, { useContext, useEffect, useState } from 'react';
import { SocketContext } from '../../contexts/SocketContext';
import DeviceCard from './DeviceCard';
import AddDeviceCard from "./AddDeviceCard";
import {Sheet, Button, Typography,} from '@mui/joy';
import RemoveDeviceDialog from "./RemoveDeviceDialog";
import AddDeviceDialog from "./AddDeviceDialog";


const DevicesTab = () => {
    const { socket, isConnected, apiHandler } = useContext(SocketContext); // Access socket and ApiHandler from context
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false); // To track loading state
    const [openAddDeviceDialog, setOpenAddDeviceDialog] = useState(false);
    const [deviceToRemove, setDeviceToRemove] = useState(null);
    const [openRemoveDialog, setOpenRemoveDialog] = useState(false);

    const [onvifCandidates, setOnvifCandidates] = useState([]);
    const [selectedCandidate, setSelectedCandidate] = useState(null);



    // Prevent multiple invocations of loadDevices
    useEffect(() => {
        if (isConnected && apiHandler && devices.length === 0) {
            loadDevices();
            scanUnknownOnvifs();
        }
    }, [isConnected, apiHandler, devices.length]); // Add devices.length to prevent multiple calls

    const loadDevices = () => {
        if (apiHandler) {
            console.log('Sending WebSocket request to load devices');
            setLoading(true); // Set loading state to true when starting to load devices
            const message = {
                method: 'api',
                type: 'get_all_devices',
                payload: { scope: 'full' },
            };

            apiHandler.sendRequest(message, {
                on_success: (data) => {
                    console.log("Request succeeded:", data);
                    setDevices(data.devices); // Update devices state
                    console.log("Updated devices:", data.devices); // Log to ensure devices are correctly set
                    setLoading(false); // Set loading to false after success
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                    setLoading(false); // Set loading to false on failure
                },
                on_loading: () => console.log("Request is loading..."),
            });
        } else {
            console.log('ApiHandler is not initialized');
        }
    };

    const scanUnknownOnvifs = () => {
        if (apiHandler) {
            console.log('Sending WebSocket request to scan new ONVIF devices');
            const message = {
                method: 'api',
                type: 'scan_onvif_devices',
                payload: { max_devices: 20 },
            };

            apiHandler.sendRequest(message, {
                on_success: (data) => {
                    console.log("Request succeeded:", data);
                    setOnvifCandidates(data.onvif_candidates);
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                },
                on_loading: () => console.log("Request is loading..."),
            });
        } else {
            console.log('ApiHandler is not initialized');
        }
    }

    const addDevice = (deviceData) => {
        return new Promise((resolve, reject) => {
            if (apiHandler) {
                let payload = { name: deviceData.name };

                let add_device_command_type
                if (deviceData.type === "legacy") {
                    add_device_command_type = "add_device";
                    payload.hw_id = deviceData.hardwareId;
                } else if (deviceData.type === "onvif") {
                    add_device_command_type = "add_onvif_device";
                    payload.ip_address = deviceData.ipAddress;
                    payload.login = deviceData.login;
                    payload.password = deviceData.password;
                    payload.prelogin_uuid = deviceData.prelogin_uuid;
                }
                const message = {
                    method: "api",
                    type: add_device_command_type,
                    payload: payload,
                };

                apiHandler.sendRequest(message, {
                    on_success: (data) => {
                        console.log("Device added:", data);
                        loadDevices(); // Reload devices after addition
                        resolve(data);
                    },
                    on_failure: (error) => {
                        console.error("Failed to add device:", error);
                        reject(error);
                    },
                    on_loading: () => console.log("Adding device..."),
                });
            } else {
                reject("ApiHandler is not initialized");
            }
        });
    };

    const removeDevice = async (deviceId) => {
        if (apiHandler) {
            const message = {
                method: 'api',
                type: 'remove_device',
                payload: { id: deviceId },
            };

            try {
                apiHandler.sendRequest(message, {
                    on_success: (data) => {
                        console.log('Device removed:', data);
                        loadDevices(); // Reload devices after addition
                    },
                    on_failure: (error) => {
                        console.error('Failed to remove device:', error);
                        loadDevices(); // Reload devices after removal
                    },
                    on_loading: () => console.log('Adding device...'),
                });
            } catch (error) {
                console.error('Failed to remove device:', error);
                throw error;
            }
        }
    };

    const handleRemoveDeviceClick = (device) => {
        setDeviceToRemove(device); // Set the device to be removed
        setOpenRemoveDialog(true); // Open the dialog
        // handleOnvifScanRequest()
    };

    return (
        <div>
            {/* Show the AddDeviceDialog */}
            <AddDeviceDialog
                open={openAddDeviceDialog}
                onClose={() => {
                    setOpenAddDeviceDialog(false)
                    setSelectedCandidate(null);
                }}
                onAddDevice={addDevice}
                candidate={selectedCandidate}
            />

            <RemoveDeviceDialog
                open={openRemoveDialog}
                device={deviceToRemove}
                onClose={() => setOpenRemoveDialog(false)}
                onRemoveDevice={removeDevice}
            />

            <div>
                <h2>Devices</h2>
                {loading ? (
                    <p>Loading devices...</p> // Show loading message while devices are being loaded
                ) : (
                    <div>
                        {devices.length === 0 ? (
                            <div>
                                <div>No devices available</div>
                                <div>
                                    <AddDeviceCard onClick={() => setOpenAddDeviceDialog(true)} />
                                </div>
                            </div>
                        ) : (
                            <div style={styles.deviceList}>
                                {devices.map((device) => (
                                    <DeviceCard
                                        key={device.id}
                                        device={device}
                                        onRemove={() => handleRemoveDeviceClick(device)}/>
                                ))}
                                <AddDeviceCard onClick={() => setOpenAddDeviceDialog(true)} />
                            </div>
                        )}
                    </div>
                )}

                <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
                    {onvifCandidates.map((candidate, index) => (
                        <Sheet key={candidate.pre_login_uuid} variant="outlined" sx={{ mb: 2, p: 2, width: 300 }}>
                            <Typography level="title-sm">UUID: {candidate.pre_login_uuid}</Typography>
                            <Typography level="body-sm">Manufacturer: {candidate.manufacturer_name}</Typography>
                            <Typography level="body-sm">Model: {candidate.model_name}</Typography>
                            <Typography level="body-sm">IP Address: {candidate.ip_address}</Typography>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                <Button size="sm" variant="outlined" color="neutral" onClick={() =>
                                    setOnvifCandidates(prev => prev.filter(c => c.pre_login_uuid !== candidate.pre_login_uuid))
                                }>Close</Button>
                                <Button size="sm" variant="solid" color="primary" sx={{ ml: 1 }} onClick={() => {
                                    setSelectedCandidate(candidate);
                                    setOpenAddDeviceDialog(true);
                                }}>Add</Button>
                            </div>
                        </Sheet>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Styling for the device list container with a responsive grid (4 per row)
const styles = {
    deviceList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '5px',
        justifyContent: 'center',
    },
};

export default DevicesTab;
